'use es6';

import 'I18n/init/data/currency';
import createStandardI18nProvider from 'I18n/init/providers/createStandardI18nProvider';
import registerAllData from 'I18n/init/register/sync/registerAllData';
import { initAppTranslations } from 'I18n/init/setup/AppInit';
import NotificationList from 'ReduxMessenger/components/NotificationList';
import UIApplication from 'UIComponents/app/UIApplication';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import { createBrowserHistory } from 'history';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import WindowDimensionsProvider from 'marketplace-ui-common/components/providers/WindowDimensionsProvider';
import MediaQueryStylesProvider from 'marketplace-ui-common/layout/components/MediaQueryStylesProvider';
import HSAnalyticsProvider from 'marketplace-ui-common/tracking/components/HSAnalyticsProvider';
import { UsageTrackerProvider } from 'marketplace-ui-common/tracking/context/UsageTrackerContext';
import Raven from 'raven-js';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import { Router } from 'react-router-dom';
import Routes from './Routes';
import { setAppContext, startApp } from './actions/AppActions';
import { getAuth, getPortalScopes } from './actions/AuthActions';
import FeedbackSurveyLoader from './components/FeedbackSurveyLoader';
import ScrollToTop from './components/ScrollToTop';
import { IN_APP } from './constants/AppContext';
import * as PortalScopes from './constants/PortalScope';
import PageMetaOverridesProvider from './context/PageMetaOverridesProvider';
import { debug, setDebugGate, setDebugKey } from './utils/env';
import store from './utils/store';
import { getRootUrl } from './utils/urls';
import { createUsageTracker } from './utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PORTAL_SCOPES_TO_CHECK = Object.values(PortalScopes).concat('global-content-write-access');
const dataFetchingClient = new DataFetchingClient();
export default class App {
  // Kicks off the app. This is intentionally not in the constructor.
  start() {
    if (debug()) {
      window.appInstance = this;
    }
    this.store = store;
    this.initialize().then(() => this.startRedux()).then(() => {
      this.startApp();
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
    return undefined;
  }
  initialize() {
    this.createUsageTracker();
    const I18nProvider = createStandardI18nProvider();
    const langAvailable = this.registerI18n(I18nProvider);
    const authReady = this.store.dispatch(getAuth()).then(({
      user,
      portal
    }) => {
      I18nProvider.setLocale({
        locale: user.locale,
        langEnabled: user.lang_enabled,
        timezone: portal.timezone
      });
      Raven.setUserContext({
        id: user.user_id,
        email: user.email,
        locale: user.locale
      });
    });
    return Promise.all([authReady, langAvailable]);
  }
  startRedux() {
    const appContext = this.getAppContext();
    this.store.dispatch(setAppContext(appContext));
    if (appContext === IN_APP) {
      this.store.dispatch(getPortalScopes(PORTAL_SCOPES_TO_CHECK));
    }
    this.store.dispatch(startApp());
    return Promise.resolve();
  }
  createUsageTracker() {
    this.usageTracker = createUsageTracker({
      inAppOrPublic: 'in-app'
    });
  }
  registerI18n(I18nProvider) {
    return initAppTranslations([I18nProvider.register(lang), registerAllData(I18nProvider)]);
  }
  startApp() {
    this.startRouter();
  }
  renderRoutes() {
    return /*#__PURE__*/_jsx(Routes, {});
  }
  startRouter() {
    const history = createBrowserHistory({
      basename: this.getRootUrl()
    });
    const AppComponent = this.getAppComponent();
    render( /*#__PURE__*/_jsx(DataFetchingClientProvider, {
      client: dataFetchingClient,
      children: /*#__PURE__*/_jsx(Provider, {
        store: this.store,
        children: /*#__PURE__*/_jsx(RhumbProvider, {
          history: historyAdapter(history),
          config: this.getRhumbConfig(),
          children: /*#__PURE__*/_jsx(HSAnalyticsProvider, {
            children: /*#__PURE__*/_jsx(MediaQueryStylesProvider, {
              children: /*#__PURE__*/_jsx(UsageTrackerProvider, {
                tracker: this.usageTracker,
                children: /*#__PURE__*/_jsxs(WindowDimensionsProvider, {
                  children: [/*#__PURE__*/_jsx(Router, {
                    history: history,
                    children: /*#__PURE__*/_jsx(PageMetaOverridesProvider, {
                      children: /*#__PURE__*/_jsxs(AppComponent, {
                        name: this.getAppName(),
                        children: [/*#__PURE__*/_jsx(ScrollToTop, {}), /*#__PURE__*/_jsx(NotificationList, {}), this.renderRoutes()]
                      })
                    })
                  }), /*#__PURE__*/_jsx(FeedbackSurveyLoader, {})]
                })
              })
            })
          })
        })
      })
    }), document.getElementsByClassName('page')[0]);
  }
  getAppContext() {
    return IN_APP;
  }
  getAppName() {
    return 'marketplace-assets-ui';
  }
  getAppComponent() {
    return UIApplication;
  }
  getRootUrl() {
    return getRootUrl();
  }
  getRhumbConfig() {}
  setDebugKey(key, value) {
    setDebugKey(key, value);
  }
  setDebugGate(gate, value) {
    setDebugGate(gate, value);
  }
}